import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LoginProviderPopup } from "../../common/components";
import { routes } from "../../constants/routes";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertComponent } from "../../common/components";
import { useSetLoading } from "../../common/hooks/appearance";
import { handlePreneedLogin } from "../../common/routines/user-operations";
import { preNeedLogin } from "../../api/auth";

//  TODO: replace with your preneed dashboard component
import PreneedDashboard from "./preneed-dashboard/dashboard";

//  TODO: replace with your preneed login bg (or reuse admin bg)
import loginBackground from "../../assets/media/admin-login-bg-image.jpeg";

const PreneedSection: React.FC = () => {
  const preNeedDetails = useSelector((state: any) => state.session.preNeedDetails);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const checkIsPreneedLogin = () => {
    const raw = localStorage.getItem("preNeedDetails");
    if (!raw) return false;

    const stored = JSON.parse(raw);

    if (!stored?.accessToken) return false;
    if (stored?.expiryTime && stored.expiryTime <= Date.now()) return false;

    return true;
  };

  const mapErrorMessage = (err: any): string => {
    const message = err?.response?.data?.message;
    const errors = err?.response?.data?.errors;

    if (message === "Input validation failed" && Array.isArray(errors)) {
      const messages: string[] = [];
      errors.forEach((errObj: any) => {
        if (errObj.email) messages.push("Invalid Email Address");
        if (errObj.password) messages.push("Invalid Password");
      });
      return messages.join(" and ");
    }

    //  Adjust backend message keys as per preneed/planner auth API
    switch (message) {
      case "PLANNER_USER_NOT_FOUND":
        return "Planner user not found";
      case "PLANNER_PASSWORD_NOT_SET":
        return "Password is not set for this planner user";
      case "INVALID_PLANNER_CREDENTIALS":
        return "Invalid Password";
      case "Too many requests. Please try again later.":
        return "Too many requests. Please try again later.";
      default:
        return "Login failed. Please try again.";
    }
  };

  const handleLoginAPI = async (email: string, password: string) => {
    setLoading(true);
    setError("");

    try {
      //  FIX: preNeed login API expects (email, password) and x-www-form-urlencoded
      const response = await preNeedLogin(email, password);

      if (response?.data?.success === true && response?.data) {
        const userData = response?.data?.data;

        handlePreneedLogin(userData);

        setTimeout(() => {
          navigate(routes.PRENEED_DASHBOARD, { replace: true });
        }, 100);
      }
    } catch (err: any) {
      const apiError = err?.response?.data;

      if (apiError?.errorCode === "ACCOUNT_LOCKED") {
        setError(apiError.message);
        setAlertMessage(apiError.message);
        setAlertType("error");
        setAlertOpen(true);
        return;
      }

      const friendlyMessage =
        apiError?.message || mapErrorMessage(err) || "Login failed. Please try again.";

      setError(friendlyMessage);
      setAlertMessage(friendlyMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  //  Show session-expired or account-inactive message for preneed
  useEffect(() => {
    const reason = localStorage.getItem("preNeedLogoutReason");
    if (reason) {
      if (reason === "preNeed:INACTIVITY" || reason === "preNeed:SESSION_EXPIRED") {
        setAlertMessage("Your session has expired. Please log in again.");
        setAlertType("info");
      } else if (reason === "preNeed:ACCOUNT_INACTIVE") {
        setAlertMessage("Your account has been deactivated. Please contact support.");
        setAlertType("error");
      }

      if (
        reason === "preNeed:INACTIVITY" ||
        reason === "preNeed:SESSION_EXPIRED" ||
        reason === "preNeed:ACCOUNT_INACTIVE"
      ) {
        localStorage.removeItem("preNeedLogoutReason");

        dispatch({
          type: "SESSION:SET_AUTO_LOGOUT",
          payload: { userType: "preNeed", autoLogoutOfPreNeed: false },
        });

        setAlertOpen(true);

        setTimeout(() => {
          setAlertOpen(false);
        }, 3000);
      }
    }
  }, [preNeedDetails, dispatch]);

  useEffect(() => {
    if (checkIsPreneedLogin() && location.pathname !== routes.PRENEED_DASHBOARD) {
      navigate(routes.PRENEED_DASHBOARD, { replace: true, state: {} });
    }

    if (location.state && (location.state as any)?.fromLogout) {
      setAlertMessage((location.state as any)?.alertMessage);
      setAlertType((location.state as any)?.alertType);
      setAlertOpen(true);

      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 1000);
    }
  }, [location.pathname, location.state, navigate]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  if (!checkIsPreneedLogin()) {
    return (
      <>
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundImage: `url(${loginBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="preneed-login-wrapper"
        >
          <LoginProviderPopup
            open={!checkIsPreneedLogin()}
            onClose={() => setError("")}
            onLogin={handleLoginAPI}
            loading={loading}
            error={error}
            headerText="MemorialGo Preneed Planner Portal"
            bodyText="Preneed Planner Login"
            bodyContent="Enter your credentials to continue"
            onForgotPassword={() => {
              setError("");
              navigate(routes.PRENEED_FORGOT_PWD);
            }}
          />
        </div>

        <AlertComponent
          message={alertMessage}
          type={alertType ?? "info"}
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="preneed-dashboard">
      <PreneedDashboard />

      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default PreneedSection;
